import { useCallback, useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { PinCodeInputStatus } from '@src/common/ui/PinCodeInput';
import { convertGeneralContextErrorToHookForm } from '@src/common';
import { isValidVerificationCode } from '@src/domains/customers';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector, setErrors, setResetPasswordCode } from '@src/features/customers/auth/store/auth';
import { track } from '@src/services/analytics';

import { usePasswordRecovery } from './usePasswordRecovery';

type RecoveryPinFormValues = {
  code: string;
};

export const useRecoveryPinForm = (params?: { onContinue?: () => void }) => {
  const { onContinue } = params ?? {};
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const contextErrors = useAppSelector(errorsSelector);
  const { validateCode, sendResetCode, loading, recoveryEmail, recoveryCode, isCodeValid, requestLockedUntil, resend } =
    usePasswordRecovery();
  const lastValidatedCodeRef = useRef<string | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        code: z.string(),
      }),
    [],
  );

  const {
    control,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RecoveryPinFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  });

  const code = watch('code') ?? '';
  const isCodeComplete = isValidVerificationCode(code);

  useEffect(() => {
    convertGeneralContextErrorToHookForm('code', setError, contextErrors, 'valid_code');
  }, [contextErrors, setError]);

  useEffect(() => {
    track('recoveryPasswordCodeStepAccess');
  }, []);

  useEffect(() => {
    if (!isCodeComplete) {
      lastValidatedCodeRef.current = null;
    }
  }, [isCodeComplete]);

  useEffect(() => {
    if (!isCodeComplete || !recoveryEmail || loading) return;
    if (isCodeValid && recoveryCode === code) return;
    if (lastValidatedCodeRef.current === code) return;

    lastValidatedCodeRef.current = code;
    validateCode(recoveryEmail, code);
  }, [code, isCodeComplete, isCodeValid, loading, recoveryCode, recoveryEmail, validateCode]);

  const status = useMemo<PinCodeInputStatus>(() => {
    if (errors.code) return 'error';
    if (isCodeValid && isCodeComplete && recoveryCode === code) return 'success';
    return 'default';
  }, [code, errors.code, isCodeComplete, isCodeValid, recoveryCode]);

  const message = useMemo(() => {
    if (errors.code) return i18n('checkout_auth_otp_code_invalid');
    return undefined;
  }, [errors.code, i18n]);

  const isSubmitEnabled = isCodeValid && isCodeComplete && recoveryCode === code;
  const resendSeconds = resend.minutes * 60 + resend.seconds;
  const showResendCountdown = Boolean(requestLockedUntil) && !resend.canResend && resendSeconds > 0;
  const showResendLink = Boolean(requestLockedUntil) && resend.canResend;

  const handleCodeChange = useCallback(
    (value: string) => {
      setValue('code', value, { shouldValidate: false });

      if (errors.code) {
        clearErrors('code');
      }

      dispatch(setErrors('valid_code', {}));

      if (isCodeValid && value !== recoveryCode) {
        dispatch(setResetPasswordCode(undefined));
      }
    },
    [clearErrors, dispatch, errors.code, isCodeValid, recoveryCode, setValue],
  );

  const onSubmit = handleSubmit(() => {
    if (!isSubmitEnabled) return;

    track('recoveryPasswordCodeStepSubmit');
    onContinue?.();
  });

  const resendCode = useCallback(() => {
    if (!recoveryEmail || !resend.canResend) return;

    lastValidatedCodeRef.current = null;
    setValue('code', '', { shouldValidate: false });
    clearErrors('code');
    dispatch(setErrors('valid_code', {}));
    dispatch(setResetPasswordCode(undefined));
    sendResetCode(recoveryEmail);
  }, [clearErrors, dispatch, recoveryEmail, resend.canResend, sendResetCode, setValue]);

  return {
    control,
    errors,
    loading,
    isSubmitEnabled,
    recoveryEmail,
    status,
    message,
    onSubmit,
    resendCode,
    showResendCountdown,
    showResendLink,
    resendSeconds,
    handleCodeChange,
  };
};
